import React from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useDeleteRoleMutation } from "../../infrastructure";
import type { OldRole as Role } from "../../domain";

interface DeleteRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  role: Role | null;
  onSuccess?: () => void;
}

export const DeleteRoleDialog: React.FC<DeleteRoleDialogProps> = ({
  open,
  onOpenChange,
  role,
  onSuccess,
}) => {
  const { t } = useTranslation("rolesPermissions");
  const [deleteRole, { isLoading }] = useDeleteRoleMutation();

  const handleConfirm = async () => {
    const roleId = role?.id;
    if (!roleId) return;
    try {
      await deleteRole(roleId).unwrap();
      toast.success(t("deleteRoleDialog.success", { name: role.name }));
      onSuccess?.();
      onOpenChange(false);
    } catch {
      toast.error(t("deleteRoleDialog.error"));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-xl sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle className="text-lg font-semibold">
            {t("deleteRoleDialog.title")}
          </DialogTitle>
          <p className="text-sm text-gray-500 mt-1">
            {t("deleteRoleDialog.confirm", { name: role?.name ?? "" })}
          </p>
        </DialogHeader>
        <div className="flex justify-end gap-3 pt-4">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isLoading}
            className="px-4 py-5 text-primary border-primary hover:text-primary hover:bg-white cursor-pointer"
          >
            {t("deleteRoleDialog.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            loading={isLoading}
            className="bg-[#E53935] hover:bg-[#E53935] text-white px-4 py-5 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t("deleteRoleDialog.delete")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
